import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Modal, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Accounts Payable
 *
 * Tracks supplier invoices the school owes — supplier name, invoice number,
 * amount, due date, payment status, partial payments. Used to manage cashflow
 * and avoid late penalties on supplier accounts.
 *
 * Backend dependency: ApiConfig.accountsPayableList + accountsPayablePay.
 * Expected row shape:
 *   { payable_id, supplier_name, supplier_invoice_no, invoice_date, due_date,
 *     amount, amount_paid, status, description }
 */
const AccountsPayable = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", search: "" });

  // Pay-down modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payNote, setPayNote] = useState("");
  const [paying, setPaying] = useState(false);

  const fetchPayables = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        status: filters.status,
        search: filters.search,
      };
      const res = await simplePostCall(
        ApiConfig.accountsPayableList,
        JSON.stringify(payload),
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Payables fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPayables();
  }, [fetchPayables]);

  const totals = rows.reduce(
    (acc, r) => {
      const amt = parseFloat(r.amount) || 0;
      const paid = parseFloat(r.amount_paid) || 0;
      acc.outstanding += amt - paid;
      const due = r.due_date ? new Date(r.due_date) : null;
      if (due && !isNaN(due) && due < new Date() && amt > paid) {
        acc.overdue += amt - paid;
      }
      return acc;
    },
    { outstanding: 0, overdue: 0 },
  );

  const openPayModal = (row) => {
    setActiveRow(row);
    const balance = (parseFloat(row.amount) || 0) - (parseFloat(row.amount_paid) || 0);
    setPayAmount(String(balance));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayNote("");
    setShowPayModal(true);
  };

  const submitPayment = async () => {
    if (!activeRow || !payAmount || Number(payAmount) <= 0) {
      toast.warning("Enter a payment amount greater than zero.");
      return;
    }
    setPaying(true);
    try {
      const res = await simplePostCall(
        ApiConfig.accountsPayablePay,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")) || 1005,
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          payable_id: activeRow.payable_id,
          payment_amount: Number(payAmount),
          payment_date: payDate,
          payment_note: payNote,
        }),
      );
      if (res?.result) {
        toast.success("Payment recorded.");
        setShowPayModal(false);
        fetchPayables();
      } else {
        toast.error(res?.message || "Payment failed.");
      }
    } catch (err) {
      toast.error("Error recording payment.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <div className="special-header panel-white-all">
          <h4 className="page-title">Accounts Payable</h4>
        </div>

        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Total Outstanding</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
          <div className="lib-tile lib-tile-overdue">
            <span className="lib-tile-label">Overdue</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.overdue)}
            </span>
          </div>
        </div>

        <div className="lib-filter-bar">
          <Form.Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search supplier or invoice no"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button
            onClick={fetchPayables}
            disabled={loading}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>

        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Supplier</th>
                <th>Invoice No</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    No supplier payables found.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const amt = parseFloat(r.amount) || 0;
                  const paid = parseFloat(r.amount_paid) || 0;
                  const bal = amt - paid;
                  return (
                    <tr key={r.payable_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{r.supplier_name || "—"}</td>
                      <td>{r.supplier_invoice_no || "—"}</td>
                      <td>
                        {r.invoice_date
                          ? new Date(r.invoice_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        {r.due_date
                          ? new Date(r.due_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>{customer_currency} {formatNumber(amt)}</td>
                      <td>{customer_currency} {formatNumber(paid)}</td>
                      <td className="fw-bold">
                        {customer_currency} {formatNumber(bal)}
                      </td>
                      <td>{r.status || "Unpaid"}</td>
                      <td className="text-center">
                        {bal > 0 && (
                          <Button
                            size="sm"
                            onClick={() => openPayModal(r)}
                            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
                          >
                            Record Payment
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      <Modal
        show={showPayModal}
        onHide={() => !paying && setShowPayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeRow && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div><b>{activeRow.supplier_name}</b></div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Invoice {activeRow.supplier_invoice_no} • Balance{" "}
                {customer_currency}{" "}
                {formatNumber(
                  (parseFloat(activeRow.amount) || 0) -
                    (parseFloat(activeRow.amount_paid) || 0),
                )}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Payment Amount <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              placeholder="Cheque #, M-Pesa code, bank ref..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPayModal(false)}
            disabled={paying}
          >
            Cancel
          </Button>
          <Button
            onClick={submitPayment}
            disabled={paying}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {paying ? <Spinner size="sm" /> : "Record Payment"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AccountsPayable;
